import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { useHardMatches } from '../../hooks/useHardMatches';
import { useUserProfile } from '../../hooks/useUserProfile';
import { supabase } from '../../lib/supabase';
import { Copy, ExternalLink, MapPin, Euro, Home, Maximize2, FileText, ChevronLeft, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getRealEstatePlatformMetadata } from '../../models/RealEstatePlatform';
import { useFlatFeature } from '../../models/FlatFeatures';
import routes from '../../modules/routes/Routes';
import ErrorBoundary from './components/ErrorBoundary';
import { AppPageLayout } from './components/PageLayout';
import SubscriptionManagement from './profilePages/SubscriptionManagement';

const MatchDetailsPage: React.FC = () => {
    const { matchId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { hardMatches, updateHardMatchState } = useHardMatches();
    const { userProfile, subscriptionData } = useUserProfile();

    const [isApplying, setIsApplying] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [communicationProposal, setCommunicationProposal] = useState<{ message?: string, error: boolean } | null>(null);
    const [isImageExpanded, setIsImageExpanded] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

    const match = useMemo(() => hardMatches.find((m) => String(m.id) === String(matchId)), [hardMatches, matchId]);
    const realEstatePlatform = match ? getRealEstatePlatformMetadata(match.flat.site) : null;
    const getFlatFeature = useFlatFeature();

    useEffect(() => {
        if (!match && hardMatches.length > 0) {
            navigate(routes.app.matches.build());
        }
    }, [match, hardMatches, navigate]);

    if (!match) {
        return null;
    }

    const handleApply = async () => {
        // Check if user has an active subscription
        if (!subscriptionData?.isSubscribed) {
            setShowSubscriptionModal(true);
            return;
        }

        setIsApplying(true);
        if (userProfile?.persons?.[0]?.name) {
            try {
                const { data, error } = await supabase.functions.invoke('generate-communication-proposal', {
                    body: { flat: match.flat }
                });
                if (error) throw new Error('Failed to generate communication proposal');
                setCommunicationProposal(data);
            } catch (error) {
                console.error('Error generating communication proposal:', error);
                setCommunicationProposal({ error: true });
            }
        }
    };

    const handleCancel = () => {
        setIsApplying(false);
        setCommunicationProposal(null);
    };

    const handleHide = async () => {
        await updateHardMatchState(match.id, 'hidden');
        navigate(routes.app.matches.build());
    };

    const handleDone = async () => {
        await updateHardMatchState(match.id, 'applied');
        navigate(routes.app.matches.build());
    };

    const handleCopy = () => {
        if (communicationProposal?.message) {
            navigator.clipboard.writeText(communicationProposal.message);
        }
    };

    const handleOpenFlatUrl = () => {
        // Check if user has an active subscription
        if (!subscriptionData?.isSubscribed) {
            setShowSubscriptionModal(true);
            return;
        }

        if (match.flat.link) {
            window.open(match.flat.link, '_blank');
        }
    };

    const images = match.flat.image_urls ?? [];
    const canNavigateImages = images.length > 1;

    return (
        <AppPageLayout currentPage="matches" navbarStart={
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
            >
                <ChevronLeft className="w-5 h-5" />
                {t('components.matchDetailsDialog.back')}
            </button>
        }>
            <div className="min-h-screen bg-gray-50">

                <div className="max-w-6xl mx-auto px-4 py-6">
                    <ErrorBoundary>
                        {/* Image Gallery */}
                        {images.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
                                <div className={`relative ${isImageExpanded ? 'max-h-[70vh]' : 'h-72 sm:h-80 md:h-96'}`}>
                                    <img
                                        src={images[currentImageIndex]}
                                        alt={match.flat.title}
                                        className={`w-full ${isImageExpanded ? 'object-contain max-h-[70vh]' : 'h-full object-cover'}`}
                                    />
                                    <div className="absolute top-4 right-4 flex items-center gap-2">
                                        <button
                                            onClick={() => setIsImageExpanded(prev => !prev)}
                                            className="bg-black/70 text-white px-3 py-1 rounded-md text-sm flex items-center gap-2"
                                        >
                                            {isImageExpanded ? (
                                                <>
                                                    <Minimize2 className="w-4 h-4" />
                                                    {t('components.matchDetailsDialog.collapse')}
                                                </>
                                            ) : (
                                                <>
                                                    <Maximize2 className="w-4 h-4" />
                                                    {t('components.matchDetailsDialog.expand')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {canNavigateImages && (
                                        <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm">
                                            {currentImageIndex + 1} / {images.length}
                                        </div>
                                    )}
                                </div>
                                {canNavigateImages && (
                                    <div className="flex gap-2 p-4 overflow-x-auto">
                                        {images.map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={img}
                                                alt={`Bild ${idx + 1}`}
                                                className={`w-20 h-20 object-cover rounded-lg cursor-pointer transition ${
                                                    currentImageIndex === idx ? 'ring-2 ring-blue-600' : 'opacity-60 hover:opacity-100'
                                                }`}
                                                onClick={() => setCurrentImageIndex(idx)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* Main Content */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Title & Basic Info */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{match.flat.title}</h1>
                                        
                                        {subscriptionData?.isSubscribed && (
                                            <div className="bg-gray-100 px-3 py-1 rounded-md text-sm font-medium text-gray-700">
                                                {realEstatePlatform?.title || match.flat.site}
                                            </div>
                                        )}
                                    </div>

                                    {match.flat.city && (
                                        <div className="flex items-center gap-2 text-gray-600 mb-6">
                                            <MapPin className="w-5 h-5" />
                                            <span className="text-base sm:text-lg">{[match.flat.street, match.flat.district, match.flat.city].filter(Boolean).join(', ')}</span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                                            <Euro className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                {match.flat.warm_rent ? `${match.flat.warm_rent}€` : match.flat.cold_rent ? `${(match.flat.cold_rent * 1.15).toFixed(0)}€` : t('components.matchDetailsDialog.onRequest')}
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">
                                                {match.flat.cold_rent ? `${match.flat.cold_rent}€ ${t('components.matchDetailsDialog.cold')}` : '—'}
                                            </div>
                                        </div>
                                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                                            <Home className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">{match.flat.rooms}</div>
                                            <div className="text-xs sm:text-sm text-gray-600">{t('components.matchDetailsDialog.rooms')}</div>
                                        </div>
                                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                                            <Maximize2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">{match.flat.size} m²</div>
                                            <div className="text-xs sm:text-sm text-gray-600">{t('components.matchDetailsDialog.size')}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Description / Reason */}
                                {match.soft_match_reason && (
                                    <div className="bg-white rounded-xl shadow-sm p-6">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{t('components.matchDetailsDialog.description')}</h2>
                                        <p className="text-gray-700 leading-relaxed">{match.soft_match_reason}</p>
                                    </div>
                                )}

                                {/* Features */}
                                {match.flat.features_unique.length > 0 && (
                                    <div className="bg-white rounded-xl shadow-sm p-6">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{t('components.matchDetailsDialog.features')}</h2>
                                        <div className="grid grid-cols-2 gap-3">
                                            {match.flat.features_unique.map((feature, index) => {
                                                const { name, icon: Icon } = getFlatFeature(feature);
                                                return (
                                                    <div key={index} className="flex items-center gap-2 text-gray-700">
                                                        <Icon size={20} />
                                                        <span>{name}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Additional Description */}
                                {match.flat.additional_description && (
                                    <div className="bg-white rounded-xl shadow-sm p-6">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{t('components.matchDetailsDialog.additionalDescription')}</h2>
                                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{match.flat.additional_description}</p>
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <div className="space-y-4">
                                <div className="bg-white rounded-xl shadow-sm p-6 lg:sticky lg:top-24">
                                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">{t('components.matchDetailsDialog.actions')}</h3>

                                    <button
                                        onClick={async () => {
                                            await handleApply();
                                        }}
                                        data-testid="match-details-apply-button"
                                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2 mb-3"
                                        data-umami-event="Matching Dialog Apply"
                                    >
                                        <FileText className="w-5 h-5" />
                                        {t('components.matchDetailsDialog.apply')}
                                    </button>

                                    <button
                                        onClick={handleOpenFlatUrl}
                                        data-testid="match-details-open-flat-button"
                                        className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition flex items-center justify-center gap-2"
                                    >
                                        <ExternalLink className="w-5 h-5" />
                                        {t('components.matchDetailsDialog.openFlat')}
                                    </button>

                                    <div className="flex gap-2 mt-4">
                                        <Button
                                            color="ghost"
                                            onClick={handleHide}
                                            className="flex-1"
                                            data-umami-event="Matching Dialog Ignore"
                                            data-testid="match-details-hide-button"
                                        >
                                            {t('components.matchDetailsDialog.notInterested')}
                                        </Button>
                                        <Button
                                            color="success"
                                            onClick={handleDone}
                                            className="flex-1"
                                            data-umami-event="Matching Dialog Apply Done"
                                            data-testid="match-details-applied-button"
                                        >
                                            {t('components.matchDetailsDialog.iApplied')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ErrorBoundary>
                </div>

                {/* Application Modal */}
                {isApplying && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-md w-full p-6">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{t('components.matchDetailsDialog.createApplication')}</h3>
                            <p className="text-gray-600 mb-6">{t('components.matchDetailsDialog.applyInstructions')}</p>
                            {userProfile?.persons?.[0]?.name ? (
                                communicationProposal?.message ? (
                                    <div className="mb-4">
                                        <div className="bg-base-200 p-4 rounded-lg whitespace-pre-wrap text-sm sm:text-base">{communicationProposal.message}</div>
                                        <Button color="primary" size="sm" className="mt-2 flex items-center gap-2" data-umami-event="Matching Dialog Apply Copy-text" onClick={handleCopy}>
                                            <Copy size={18} />
                                            {t('components.matchDetailsDialog.copyText')}
                                        </Button>
                                    </div>
                                ) : communicationProposal?.error ? (
                                    <p className="mb-4 text-error">{t('components.matchDetailsDialog.failedToGenerate')}</p>
                                ) : (
                                    <div className="flex items-center mb-4">
                                        <span className="loading loading-spinner loading-md mr-2"></span>
                                        <p>{t('components.matchDetailsDialog.generatingText')}</p>
                                    </div>
                                )
                            ) : (
                                <>
                                    <p className="mb-4">{t('components.matchDetailsDialog.fillProfilePrompt')}</p>
                                    <Button size="sm" color="primary" onClick={() => navigate(routes.app.profile.personalProfile.build())}>{t('components.matchDetailsDialog.goToProfile')}</Button>
                                </>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={handleCancel}
                                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition"
                                    data-umami-event="Matching Dialog Apply Cancel"
                                >
                                    {t('components.matchDetailsDialog.cancel')}
                                </button>
                                <button
                                    onClick={handleDone}
                                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
                                >
                                    {t('components.matchDetailsDialog.iApplied')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Subscription Modal */}
                {showSubscriptionModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                                        {t('components.matchDetailsDialog.subscriptionRequired')}
                                    </h3>
                                    <button
                                        onClick={() => setShowSubscriptionModal(false)}
                                        className="text-gray-400 hover:text-gray-600 text-2xl"
                                    >
                                        ×
                                    </button>
                                </div>
                                <SubscriptionManagement />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppPageLayout>
    );
};

export default MatchDetailsPage;

